import React, { useState, useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';

import * as REST from '~services/rest';

import ServerContext from '~src/serverContext';

import NotFound from '~components/not-found';
import City from '~containers/city';

import FullLoader from '~components/loader/full-loader';
import { readCache, writeCache } from '~helpers/sessionCache';

// Resolved city slugs change infrequently, so cache them client-side. On a return
// navigation (no SSR serverLocation) this avoids blocking on a slug-resolution request.
const ONE_DAY = 24 * 60 * 60 * 1000;
const slugFromPath = (pathname) => pathname.substring(1).toLowerCase().replace(/\/+$/g, '');
const slugCacheKey = (pathname) => `city-slug:${slugFromPath(pathname)}`;

const Dynamic = () => {
  const intl = useIntl();
  const history = useHistory();

  const urlLocation = useLocation();
  const cache = useContext(ServerContext);

  const serverLocation = cache.get('serverLocation');
  const serverCityNotFound = cache.get('cityNotFound');

  // Fall back to a previously resolved slug so client navigation can render immediately.
  const cachedSlugLocation = serverLocation || readCache(slugCacheKey(urlLocation.pathname), ONE_DAY);

  const [locationDetails, setLocationDetails] = useState(cachedSlugLocation || null);
  const [isNotFound, setIsNotFound] = useState(serverCityNotFound || false);
  const [isValidatingSlug, setIsValidatingSlug] = useState(serverCityNotFound ? false : !cachedSlugLocation);

  useEffect(async () => {
    if (urlLocation.pathname.trim() === '/search') {
      history.replace('/');
      return;
    }

    if (serverLocation || serverCityNotFound) {
      setTimeout(() => {
        cache.del('serverLocation');
        cache.del('cityNotFound');
        cache.del('cityIsZeroAds');
        cache.del('cityAiContent');
      }, 0);

      setIsValidatingSlug(false);
      return;
    }

    const slug = slugFromPath(urlLocation.pathname);

    // Already have a cached resolution for the current slug; render now, skip the request.
    const cached = readCache(slugCacheKey(urlLocation.pathname), ONE_DAY);
    if (cached && cached.slug === slug) {
      setLocationDetails(cached);
      setIsNotFound(false);
      setIsValidatingSlug(false);
      return;
    }

    if (!urlLocation.pathname) {
      setIsValidatingSlug(false);
      return;
    }

    setIsValidatingSlug(true);

    try {
      if (slug.substring(0, 1) === '&' || slug.substring(0, 1) === '?') {
        history.replace('/');
        return;
      }

      const response = await REST.get({
        name: `locations`,
        params: {
          slug,
          isCity: true,
        },
        shouldThrowError: true,
      });

      const place = response.data;

      const resolved = {
        description: place.city,
        slug,
        locationId: place.place_id,
      };
      setLocationDetails(resolved);
      writeCache(slugCacheKey(urlLocation.pathname), resolved);
      setIsNotFound(false);
    } catch (err) {
      console.log(err, 'Error while fetching cities');
      setIsNotFound(true);
    } finally {
      setIsValidatingSlug(false);
    }
  }, [urlLocation.pathname]);

  return (
    <>
      {!isValidatingSlug ? (
        isNotFound ? (
          <NotFound
            headline={intl.formatMessage({
              id: 'components.not-found.headline',
            })}
            content={intl.formatMessage({
              id: 'components.not-found.content',
            })}
          />
        ) : (
          // Key by slug so the per-mount city snapshots cannot carry over between cities.
          <City key={locationDetails?.slug || urlLocation.pathname} location={locationDetails} />
        )
      ) : (
        <FullLoader />
      )}
    </>
  );
};

export default Dynamic;
